import { useEffect, useState } from 'react'
import { supabase, getPaymentsForExport } from '@unvld/db'
import { useAuth } from '../stores/auth'
import { formatEUR } from '@unvld/stripe'
import { TierGate } from '../components/TierGate'
import { toast } from 'sonner'
import {
  TrendingUp,
  Users,
  Ticket,
  CalendarDays,
  ArrowUpRight,
  ArrowDownRight,
  Download,
} from 'lucide-react'
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns'
import { de } from 'date-fns/locale'

interface RevenueData {
  date: string
  amount: number
}

interface GuestTypeBreakdown {
  type: string
  count: number
  pct: number
}

interface MonthlyStats {
  total_revenue: number
  total_tickets: number
  total_events: number
  unique_guests: number
  avg_ticket_price: number
  capacity_utilization: number
}

function formatDENumber(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',')
}

async function exportRevenueCSV(from: Date, to: Date) {
  const { data: payments, error } = await getPaymentsForExport({
    from: from.toISOString(),
    to: to.toISOString(),
  })

  if (error || !payments) {
    toast.error('Export fehlgeschlagen')
    return
  }

  if (payments.length === 0) {
    toast.error('Keine Zahlungen im Zeitraum')
    return
  }

  const headers = ['Datum', 'Event', 'Ticketnummer', 'Brutto (EUR)', 'Plattformgebühr (EUR)', 'Stripe-Gebühr (EUR)', 'Netto (EUR)', 'Status']
  const STATUS_MAP: Record<string, string> = {
    succeeded: 'Erfolgreich',
    pending: 'Ausstehend',
    failed: 'Fehlgeschlagen',
    refunded: 'Erstattet',
  }
  const rows = payments.map((p: any) => {
    const ticket = p.tickets
    const event = ticket?.events
    const stripeFee = p.stripe_fee_cents ?? 0
    const platformFee = p.platform_fee_cents ?? 0
    const netto = p.amount_cents - platformFee - stripeFee
    return [
      new Date(p.created_at).toLocaleDateString('de-DE'),
      event?.title || '',
      ticket?.ticket_number || '',
      formatDENumber(p.amount_cents),
      formatDENumber(platformFee),
      formatDENumber(stripeFee),
      formatDENumber(netto),
      STATUS_MAP[p.status] || p.status,
    ]
  })

  const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(';')).join('\n')
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `umsatz_${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
  toast.success(`${payments.length} Zahlungen exportiert`)
}

export function AnalyticsPage() {
  const { org } = useAuth()
  const [period, setPeriod] = useState<'30d' | '90d' | 'month'>('30d')
  const [stats, setStats] = useState<MonthlyStats | null>(null)
  const [revenueData, setRevenueData] = useState<RevenueData[]>([])
  const [guestBreakdown, setGuestBreakdown] = useState<GuestTypeBreakdown[]>([])
  const [topEvents, setTopEvents] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    if (org) loadAnalytics()
  }, [org, period])

  const getDateRange = () => {
    const now = new Date()
    if (period === '30d') return { from: subDays(now, 30), to: now }
    if (period === '90d') return { from: subDays(now, 90), to: now }
    return { from: startOfMonth(now), to: endOfMonth(now) }
  }

  const loadAnalytics = async () => {
    setLoading(true)
    const { from, to } = getDateRange()
    const fromStr = from.toISOString()
    const toStr = to.toISOString()

    try {
      // Revenue + ticket stats
      const { data: payments } = await supabase
        .from('payments')
        .select('amount_cents, platform_fee_cents, created_at')
        .eq('status', 'succeeded')
        .gte('created_at', fromStr)
        .lte('created_at', toStr)
        .order('created_at')

      const totalRevenue = payments?.reduce((s, p) => s + p.amount_cents, 0) ?? 0

      // Tickets in period
      const { data: tickets, count: ticketCount } = await supabase
        .from('tickets')
        .select('*, price_tiers(guest_type)', { count: 'exact' })
        .in('status', ['valid', 'checked_in'])
        .gte('created_at', fromStr)
        .lte('created_at', toStr)

      // Events in period
      const { count: eventCount } = await supabase
        .from('events')
        .select('id', { count: 'exact' })
        .in('status', ['published', 'completed', 'sold_out'])
        .gte('date', from.toISOString().split('T')[0])
        .lte('date', to.toISOString().split('T')[0])

      // Unique guests
      const guestIds = new Set(tickets?.map((t) => t.guest_id).filter(Boolean))

      // Avg ticket price
      const avgPrice = ticketCount && ticketCount > 0 ? totalRevenue / ticketCount : 0

      setStats({
        total_revenue: totalRevenue,
        total_tickets: ticketCount ?? 0,
        total_events: eventCount ?? 0,
        unique_guests: guestIds.size,
        avg_ticket_price: avgPrice,
        capacity_utilization: 0,
      })

      // Revenue by day
      const byDay: Record<string, number> = {}
      payments?.forEach((p) => {
        const day = p.created_at.split('T')[0]
        byDay[day] = (byDay[day] || 0) + p.amount_cents
      })
      setRevenueData(
        Object.entries(byDay)
          .map(([date, amount]) => ({ date, amount }))
          .sort((a, b) => a.date.localeCompare(b.date))
      )

      // Guest type breakdown
      const typeCounts: Record<string, number> = {}
      tickets?.forEach((t) => {
        const gt = (t.price_tiers as any)?.guest_type || 'unknown'
        typeCounts[gt] = (typeCounts[gt] || 0) + 1
      })
      const total = Object.values(typeCounts).reduce((s, c) => s + c, 0)
      setGuestBreakdown(
        Object.entries(typeCounts).map(([type, count]) => ({
          type,
          count,
          pct: total > 0 ? Math.round((count / total) * 100) : 0,
        }))
      )

      // Top events by revenue
      const { data: topEventsData } = await supabase
        .from('events')
        .select('id, title, date, capacity')
        .in('status', ['published', 'completed', 'sold_out'])
        .gte('date', from.toISOString().split('T')[0])
        .order('date', { ascending: false })
        .limit(5)

      if (topEventsData) {
        const enriched = await Promise.all(
          topEventsData.map(async (ev) => {
            const { count: sold } = await supabase
              .from('tickets')
              .select('id', { count: 'exact' })
              .eq('event_id', ev.id)
              .in('status', ['valid', 'checked_in'])

            const { data: rev } = await supabase
              .from('payments')
              .select('amount_cents')
              .eq('status', 'succeeded')
              .in(
                'ticket_id',
                (
                  await supabase
                    .from('tickets')
                    .select('id')
                    .eq('event_id', ev.id)
                ).data?.map((t) => t.id) ?? []
              )

            const revenue = rev?.reduce((s, p) => s + p.amount_cents, 0) ?? 0
            return { ...ev, sold: sold ?? 0, revenue }
          })
        )
        setTopEvents(enriched)
      }
    } catch (err) {
      console.error('Analytics error:', err)
    }

    setLoading(false)
  }

  const TYPE_LABELS: Record<string, string> = {
    couple: 'Paare',
    single_male: 'Einzelherren',
    single_female: 'Einzeldamen',
    group: 'Gruppen',
    unknown: 'Sonstige',
  }

  const TYPE_COLORS: Record<string, string> = {
    couple: 'bg-violet-500',
    single_male: 'bg-blue-500',
    single_female: 'bg-pink-500',
    group: 'bg-amber-500',
    unknown: 'bg-slate-500',
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-white">Analytics</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              setExporting(true)
              const { from, to } = getDateRange()
              await exportRevenueCSV(from, to)
              setExporting(false)
            }}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/15 disabled:opacity-40 text-white text-sm rounded-lg transition-colors"
          >
            {exporting ? (
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
            Export
          </button>
          <div className="flex bg-slate-900 border border-slate-800 rounded-lg p-1">
            {[
              { key: '30d', label: '30 Tage' },
              { key: '90d', label: '90 Tage' },
              { key: 'month', label: 'Monat' },
            ].map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key as any)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  period === p.key
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            icon={TrendingUp}
            label="Umsatz"
            value={formatEUR(stats.total_revenue)}
            color="emerald"
          />
          <KPICard
            icon={Ticket}
            label="Tickets verkauft"
            value={String(stats.total_tickets)}
            color="indigo"
          />
          <KPICard
            icon={Users}
            label="Einzigartige Gäste"
            value={String(stats.unique_guests)}
            color="violet"
          />
          <KPICard
            icon={CalendarDays}
            label="Events"
            value={String(stats.total_events)}
            sub={`Ø ${formatEUR(stats.avg_ticket_price)} pro Ticket`}
            color="rose"
          />
        </div>
      )}

      <TierGate feature="analytics">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Chart (simplified bar chart) */}
          <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Umsatzverlauf</h2>
            {revenueData.length > 0 ? (
              <div className="flex items-end gap-1 h-48">
                {(() => {
                  const max = Math.max(...revenueData.map((d) => d.amount), 1)
                  return revenueData.map((d) => (
                    <div
                      key={d.date}
                      className="flex-1 group relative"
                      title={`${format(new Date(d.date), 'd. MMM', { locale: de })}: ${formatEUR(d.amount)}`}
                    >
                      <div
                        className="bg-indigo-500/80 hover:bg-indigo-400 rounded-t transition-colors w-full"
                        style={{ height: `${Math.max(2, (d.amount / max) * 100)}%` }}
                      />
                    </div>
                  ))
                })()}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-slate-500">
                Keine Umsatzdaten im Zeitraum
              </div>
            )}
            {revenueData.length > 0 && (
              <div className="flex justify-between mt-2 text-xs text-slate-500">
                <span>{format(new Date(revenueData[0].date), 'd. MMM', { locale: de })}</span>
                <span>{format(new Date(revenueData[revenueData.length - 1].date), 'd. MMM', { locale: de })}</span>
              </div>
            )}
          </div>

          {/* Guest Type Breakdown */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Gästetypen</h2>
            {guestBreakdown.length > 0 ? (
              <div className="space-y-4">
                {guestBreakdown.map((g) => (
                  <div key={g.type}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-300">
                        {TYPE_LABELS[g.type] || g.type}
                      </span>
                      <span className="text-sm text-white font-medium">
                        {g.count} ({g.pct}%)
                      </span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${TYPE_COLORS[g.type] || 'bg-slate-500'}`}
                        style={{ width: `${g.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-slate-500 text-sm">
                Keine Daten
              </div>
            )}
          </div>
        </div>

        {/* Top Events */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-800">
            <h2 className="text-lg font-semibold text-white">Top Events</h2>
          </div>
          {topEvents.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left px-4 py-3 text-xs text-slate-400">Event</th>
                  <th className="text-left px-4 py-3 text-xs text-slate-400">Datum</th>
                  <th className="text-left px-4 py-3 text-xs text-slate-400">Verkauft</th>
                  <th className="text-left px-4 py-3 text-xs text-slate-400">Auslastung</th>
                  <th className="text-right px-4 py-3 text-xs text-slate-400">Umsatz</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {topEvents.map((ev) => {
                  const utilPct = ev.capacity > 0 ? Math.round((ev.sold / ev.capacity) * 100) : 0
                  return (
                    <tr key={ev.id} className="hover:bg-slate-800/30">
                      <td className="px-4 py-3 text-white font-medium">{ev.title}</td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {format(new Date(ev.date), 'd. MMM yyyy', { locale: de })}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {ev.sold} / {ev.capacity}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                utilPct >= 90 ? 'bg-emerald-500' : utilPct >= 50 ? 'bg-indigo-500' : 'bg-amber-500'
                              }`}
                              style={{ width: `${utilPct}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-400">{utilPct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-white font-medium">
                        {formatEUR(ev.revenue)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8 text-slate-500">Keine Events im Zeitraum</div>
          )}
        </div>
      </TierGate>
    </div>
  )
}

function KPICard({
  icon: Icon,
  label,
  value,
  sub,
  color,
}: {
  icon: any
  label: string
  value: string
  sub?: string
  color: string
}) {
  const colorMap: Record<string, string> = {
    indigo: 'bg-indigo-500/10 text-indigo-400',
    violet: 'bg-violet-500/10 text-violet-400',
    emerald: 'bg-emerald-500/10 text-emerald-400',
    rose: 'bg-rose-500/10 text-rose-400',
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorMap[color]}`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-sm text-slate-400">{label}</p>
          {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  )
}
